// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import * as Sentry from '@sentry/react';
import { api } from '@/api/axios-instance';
import { handleAPIError } from '@/api/error-utils';
import { DownloadRounded } from '@/constants/hardfin-standard-icons';
import { getFilenameFromContentDisposition } from '@/file/utilities/get-filename-from-content-disposition';
import { Button, CircularProgress } from '@mui/material';
import { AxiosError } from 'axios';
import { useState } from 'react';

interface Props {
  invoiceKey: string;
  invoiceNumber: string;
  onError?: () => void;
}

export function DownloadPDFButton(props: Props) {
  const [isPending, setIsPending] = useState(false);

  const handleClick = async () => {
    if (isPending) {
      return;
    }

    setIsPending(true);

    try {
      const response = await api.get(`/v1/invoice/${props.invoiceKey}/pdf`, {
        responseType: 'blob',
      });

      // Validate that we received a PDF response
      const contentType = response.headers['content-type'];
      if (
        typeof contentType !== 'string' ||
        !contentType.includes('application/pdf')
      ) {
        throw new Error(`Expected PDF response but received: ${contentType}`);
      }

      const contentDisposition = response.headers['content-disposition'];
      const filename =
        getFilenameFromContentDisposition(contentDisposition) ??
        `${props.invoiceNumber}.pdf`;

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const apiError = handleAPIError(error as AxiosError);
      if (!apiError.sentToSentry) {
        Sentry.captureException(error);
      }
      props.onError?.();
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Button
      size="small"
      variant="contained"
      color="primary"
      startIcon={
        isPending ? (
          <CircularProgress size={16} />
        ) : (
          <DownloadRounded fontSize="small" />
        )
      }
      disabled={isPending}
      onClick={handleClick}
    >
      Download PDF
    </Button>
  );
}
